import { SerializedException } from '../../models';

export const STATUS_POLL_INTERVAL_MS = 4000;
export const MAX_CONSECUTIVE_STATUS_FAILURES = 6;

export interface StatusPollDecision {
  consecutiveFailures: number;
  delayMs: number;
  stop: boolean;
}

export const statusPollDecisionForSuccess = (): StatusPollDecision => ({
  consecutiveFailures: 0,
  delayMs: STATUS_POLL_INTERVAL_MS,
  stop: false,
});

const retryAfterDelay = (retryAfter: string | undefined, now: number): number => {
  if (!retryAfter) return STATUS_POLL_INTERVAL_MS;
  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000;
  }
  const retryAt = Date.parse(retryAfter);
  return Number.isFinite(retryAt)
    ? Math.max(retryAt - now, 0)
    : STATUS_POLL_INTERVAL_MS;
};

export const statusPollDecisionForFailure = (
  error: SerializedException | undefined,
  currentConsecutiveFailures: number,
  now = Date.now(),
): StatusPollDecision => {
  if (error?.status === 429) {
    return {
      consecutiveFailures: 0,
      delayMs: retryAfterDelay(error.retryAfter, now),
      stop: false,
    };
  }

  const isServerFailure = error?.status !== undefined
    && error.status >= 500
    && error.status <= 599;
  const isTimeout = error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT';
  const isNoResponse = error?.hasResponse === false;
  if (isServerFailure || isTimeout || isNoResponse) {
    const consecutiveFailures = currentConsecutiveFailures + 1;
    return {
      consecutiveFailures,
      delayMs: STATUS_POLL_INTERVAL_MS,
      stop: consecutiveFailures >= MAX_CONSECUTIVE_STATUS_FAILURES,
    };
  }

  // Auth/domain 4xx and any non-qualifying serialized error stay with their
  // existing handlers. They break a consecutive infrastructure-failure streak.
  return {
    consecutiveFailures: 0,
    delayMs: STATUS_POLL_INTERVAL_MS,
    stop: false,
  };
};
